import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4797Sales = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 4797 Sales of Business Property | TaxZerone Guide"
          description="Learn about Form 4797 for reporting business property sales, exchanges, or conversions. Find filing requirements, steps, and common questions with TaxZerone."
          keywords=""
          canonical="resources/tax-exempt/form-4797-sales-business-property/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 4797{' '}
          <span className="highlight-container">
            <span className="highlight">Sales of Business Property </span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-4797-sales-business-property/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4797 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 4797
                        </strong>{' '}
                        is used to report the
                        <strong style={{ fontWeight: 600 }}>
                          sale, exchange, or involuntary conversion of business
                          property
                        </strong>
                        , including depreciable and amortizable property.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>
                        Taxpayers and entities that
                        <strong style={{ fontWeight: 600 }}>
                          sell, exchange, or otherwise dispose of business
                          property{' '}
                        </strong>{' '}
                        may need to complete Form 4797 when the transaction
                        meets the IRS reporting requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 4797 reports
                        <strong style={{ fontWeight: 600 }}>
                          sales proceeds, adjusted basis, depreciation, gains
                          and losses, Section 1231 transactions,{' '}
                        </strong>
                        , and applicable
                        <strong style={{ fontWeight: 600 }}>
                          recapture amounts.
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:{' '}
                        </strong>{' '}
                        Form 4797 may be required or used with returns such as{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1065-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1065
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1120-s/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1120-S
                        </Link>{' '}
                        depending on the taxpayer and the applicable
                        depreciation or amortization requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 4797?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 4797?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Complete Form 4797: Line-by-Line Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 4797?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Form 4797 </span>is used by
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    businesses and individuals
                  </span>{' '}
                  to report the sale, exchange, or involuntary conversion of
                  property used in a{' '}
                  <span style={{ fontWeight: 600 }}> trade or business</span>.
                  This can include Real estate , Machinery vehicles , and Other
                  assets that have been depreciated or amortized.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The form captures detailed information about the transaction,
                  including the original cost of the property, accumulated
                  depreciation, and the sales price, to determine the{' '}
                  <span style={{ fontWeight: 600 }}>
                    gain or loss on the sale
                  </span>
                  .
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Form 4797?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 4797 must be filed by taxpayers who sell, exchange, or
                  otherwise dispose of property used in a trade or business.
                  This may include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Individuals and sole proprietors </li>
                  <li>Partnerships</li>
                  <li>Corporations </li>
                  <li>S corporations </li>
                  <li>Tax-exempt organizations </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  You may also need to file Form 4797 if:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You sell business property, such as equipment, machinery,
                    vehicles, or buildings.{' '}
                  </li>
                  <li>
                    You dispose of depreciable or amortizable business
                    assets.{' '}
                  </li>
                  <li>
                    Your property was involuntarily converted due to theft,
                    casualty, destruction, or condemnation.{' '}
                  </li>
                  <li>
                    You are filing{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-t-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-T
                    </Link>{' '}
                    and disposing of property connected to unrelated business
                    income activities
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations.
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4797sales`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                 <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howto"
                >
                  How to Complete Form 4797: Line-by-Line Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-sale-img.webp" />
                  <source srcSet="/assets/images/f4797-sale-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-sale-img.png"
                    loading="lazy"
                    alt="Form 4797 Sales of Business Property Line 1a to Line 1c"
                    width={760}
                    height={229}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In <span style={{ fontWeight: 600 }}> Form 4797</span>, enter
                  the <span style={{ fontWeight: 600 }}> taxpayer’s name</span>{' '}
                  and{' '}
                  <span style={{ fontWeight: 600 }}> identifying number,</span>{' '}
                  such as an SSN or EIN, exactly as shown on the tax return.
                  This will help the IRS identify and process the form properly.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  When a <span style={{ fontWeight: 600 }}> partnership</span>{' '}
                  or <span style={{ fontWeight: 600 }}> S corporation</span>{' '}
                  disposes of Section 179 property, each partner or shareholder
                  must report their share of the transaction using the
                  appropriate IRS forms.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Important:</span> Details,
                  such as sales proceeds, depreciation, and Section 179
                  deductions, are generally provided through{' '}
                  <Link
                    href="/resources/business-tax/schedule-k-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule K-1
                  </Link>
                  .
                </Alert>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report <span style={{ fontWeight: 600 }}> total gross</span>{' '}
                  proceeds from sales or exchanges reported on{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Forms 1099-S
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1099-B
                  </Link>{' '}
                  that are included on Form 4797.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of gains from partial dispositions of
                  MACRS property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of losses from partial dispositions of
                  MACRS property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part I - Sales or Exchanges of Property Used in a Trade or
                  Business and Involuntary Conversions from Other Than Casualty
                  or Theft-Most Property Held More Than 1 Year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part1-img.webp" />
                  <source srcSet="/assets/images/f4797-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part1-img.png"
                    loading="lazy"
                    alt="Form 4797 Part I for reporting sales, exchanges, and involuntary conversions of business property held for more than one year from Line 2 to Line 6"
                    width={760}
                    height={210}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part I of <span style={{ fontWeight: 600 }}> Form 4797</span>{' '}
                  is used to report Section 1231 transactions related to
                  business property held for more than one year. It usually
                  encompasses the sale, exchange, or involuntary conversion of
                  assets of the trade or business, such as real estate,
                  depreciable property, livestock for use in a trade or
                  business, timber, and some crops.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  But it does not include items such as inventory held for sale,
                  copyrights, patents, artistic works, and government
                  publications in section 1231.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the details of each property transaction on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Description of the property sold or exchanged.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the property was acquired. If the property was
                  inherited, enter “Inherited” instead of the acquisition date.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<NoteAltOutlinedIcon sx={{ fontSize: 32 }} />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Report
                  losses in parentheses. If the property was inherited, enter
                  “INHERITED” instead of the acquisition date.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Date the property was sold or disposed of.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Gross sales price of the property.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total depreciation allowed or allowable
                  </span>{' '}
                  on the property since it was acquired. This includes
                  depreciation deductions claimed or that could have been
                  claimed during the ownership period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the original cost or other basis of the property, along
                  with improvements made and any selling expenses related to the
                  disposition of the property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the gain or loss from the transaction after adjusting
                  basis and depreciation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter gains reported from{' '}
                  <span style={{ fontWeight: 600 }}>Form 4684</span> related to
                  casualty or theft transactions in Line 39.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report Section 1231 gains from installment sales reported on{' '}
                  <span style={{ fontWeight: 600 }}> Form 6252</span> in line 26
                  or Line 37.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter gains or losses from like-kind exchanges reported on{' '}
                  <span style={{ fontWeight: 600 }}> Form 8824.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report gains if any from Line 32 and from other involuntary
                  conversions not related to casualty or theft.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part1-line7-img.webp" />
                  <source srcSet="/assets/images/f4797-part1-line7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part1-line7-img.png"
                    loading="lazy"
                    alt="Form 4797 Part I for reporting sales, exchanges, and involuntary conversions of business property held for more than one year from Line 7 to Line 9"
                    width={760}
                    height={180}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the amounts from lines 2 through 6 and enter the total
                  gain or loss. Partnerships and S corporations must report the
                  amount according to the instructions for{' '}
                  <Link
                    href="/resources/business-tax/form-1065-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 1065
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/business-tax/schedule-k-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120-S (Schedule K-1)
                  </Link>{' '}
                  and can skip lines 8, 9, 11, and 12.
                </Typography>
                <Typography variant="body1">
                  Individuals, partners, S corporation shareholders, and other
                  filers should follow these rules:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If line 7 shows a loss or zero, enter the amount on line 11
                    and skip lines 8 and 9.
                  </li>
                  <li>
                    If line 7 shows a gain and there are no prior unrecaptured
                    Section 1231 losses, the gain is generally reported as a
                    long-term capital gain on{' '}
                    <Link
                      href="/resources/business-tax/form-1120s-schedule-d/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule D (1120-S)
                    </Link>
                    .
                  </li>
                  <li>
                    If there are prior year unrecaptured Section 1231 losses,
                    continue to lines 8 and 9 to determine how the gain should
                    be treated.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Tip:</span> For more
                  details, refer to our{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1065
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120-S
                  </Link>{' '}
                  resource pages. You can also explore our IRS business tax
                  forms resource center for additional filing guidance and
                  related forms.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any Non recaptured Section 1231 losses from the previous
                  five tax years. These are prior year Section 1231 losses that
                  have not yet been offset against current Section 1231 gains.
                  Current year gains may be treated as ordinary income up to the
                  amount of these unrecaptured losses.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>For example:</strong>{' '}
                  Suppose you had Section 1231 losses of $10,000 during the
                  previous five years, and $3,000 of those losses were already
                  offset by earlier Section 1231 gains. If your current year
                  Section 1231 gain is $2,000, the remaining unrecaptured loss
                  would be $7,000. In this case, the entire $2,000 current year
                  gain is treated as ordinary income and reported on Form 4797.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 9 is used to determine whether any prior year of Section
                  1231 losses still remain to be recaptured.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}> line 9 is zero</span>,
                    the entire gain reported on line 7 is treated as a
                    recaptured{' '}
                    <span style={{ fontWeight: 600 }}> Section 1231</span> loss
                    for the current tax year.
                  </li>
                  <li>
                    If{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      line 9 is greater than zero,
                    </span>{' '}
                    it means that all prior-year{' '}
                    <span style={{ fontWeight: 600 }}>non</span> -recaptured
                    <span style={{ fontWeight: 600 }}> Section 1231</span>{' '}
                    losses have been fully{' '}
                    <span style={{ fontWeight: 600 }}> recaptured</span>. In
                    this case, enter the amount from line 8 on line 12, and
                    report the remaining gain from line 9 as a long-term capital
                    gain on{' '}
                    <Link
                      href="/resources/business-tax/form-1120s-schedule-d/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule D.
                    </Link>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part II - Ordinary Gains and Losses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part2-img.webp" />
                  <source srcSet="/assets/images/f4797-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part2-img.png"
                    loading="lazy"
                    alt="Form 4797 Part II Ordinary Gains and Losses Line 10 to Line 18b"
                    width={760}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Part II of IRS Form 4797 is utilized to disclose gains and
                  losses that cannot be accounted for using either Part I or
                  Part III. This particular section is usually applied to
                  property that was{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    acquired within one year or less
                  </span>{' '}
                  and some business transactions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report ordinary gains and losses that are not included on
                  lines 11 through 16. This may include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Property held for one year or less</li>
                  <li>
                    Losses from abandoned business or investment property{' '}
                  </li>
                  <li>
                    Income from property deducted under the de minimis safe
                    harbor rule{' '}
                  </li>
                  <li>
                    Gains or losses from securities or commodities under the
                    mark-to-market election{' '}
                  </li>
                  <li>
                    Deferred gains from qualifying electric transmission
                    property{' '}
                  </li>
                  <li>
                    Losses from Small Business Investment Company (SBIC)
                    stock{' '}
                  </li>
                  <li>Losses from Section 1244 small business stock </li>
                  <li>Certain preferred stock transactions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}> loss amount</span> carried
                  over from <span style={{ fontWeight: 600 }}> line 7.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}> gains from line 7 </span>{' '}
                  or <span style={{ fontWeight: 600 }}> line 8 </span> that must
                  be treated as ordinary income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report <span style={{ fontWeight: 600 }}>gains</span> from
                  casualty or theft transactions from{' '}
                  <span style={{ fontWeight: 600 }}>Form 4684, line 31</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>net gains or losses</span>{' '}
                  reported from Form 4684, line 31 and line 38a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    ordinary gains from installment
                  </span>{' '}
                  sales reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 6252,</span> line 25
                  and line 36.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    ordinary gains or losses{' '}
                  </span>{' '}
                  from{' '}
                  <span style={{ fontWeight: 600 }}>
                    like-kind exchanges
                  </span>{' '}
                  reported on{' '}
                  <span style={{ fontWeight: 600 }}> Form 8824</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine lines 10 through 16 and enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total ordinary gain or loss
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For individual tax returns, complete lines 18a and 18b to
                  determine the correct treatment of gains and losses reported
                  in Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete line 18a if there is a{' '}
                  <span style={{ fontWeight: 600 }}>
                    gain on Form 4797, line 3
                  </span>{' '}
                  , a{' '}
                  <span style={{ fontWeight: 600 }}>
                    loss on Form 4797, line 11,
                  </span>{' '}
                  and a loss on Form 4684, line 35, column (b)(ii). Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    smaller of the two losses
                  </span>{' '}
                  from Form 4797, line 11, or Form 4684, line 35, column
                  (b)(ii), treating both amounts as positive numbers for
                  comparison purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the loss from income-producing property on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule A (Form 1040)
                  </span>
                  , line 16, and identify it as “Form 4797, line 18a.” Do not
                  include losses related to employee-use property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18b
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Recalculate the gain or loss from line 17 after excluding any
                  loss entered on line 18a. Enter the updated amount on{' '}
                  <span style={{ fontWeight: 600 }}>line 18b</span> and report
                  it on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule 1 (Form 1040)
                  </span>
                  , Part I, line 4.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part III - Gain from Disposition of Property Under Sections
                  1245, 1250, 1252, 1254, and 1255
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part3-img.webp" />
                  <source srcSet="/assets/images/f4797-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part3-img.png"
                    loading="lazy"
                    alt="Form 4797 Part III Gain from Disposition of Property from Line 19 to Line 25b"
                    width={760}
                    height={308}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Depreciation recapture and any other gains that need to be
                  reported as ordinary income upon the sale or other disposition
                  of specific business assets can be calculated using Part III
                  of Form 4797. Property held for{' '}
                  <span style={{ fontWeight: 600 }}> one year or less</span> is
                  generally
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    reported in Part II instead of Part III
                  </span>
                  .
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(0, 78, 102)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}> Tip: </span> Partners and S
                  corporation shareholders reporting Section 179 property
                  dispositions should use the details provided on{' '}
                  <Link
                    href="/resources/business-tax/schedule-k1-1065/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule K-1 (Form 1120-S or Form 1065)
                  </Link>
                  .
                </Alert>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report details of property disposed of under Sections 1245,
                  1250, 1252, 1254, or 1255 on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a description of the property sold or disposed of.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the property was acquired.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date the property was sold or disposed of.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> total consideration</span>{' '}
                  for the transfer of the property, including any cash, FMV of
                  property acquired, and mortgage or indebtedness assumed by the
                  purchaser.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In casualty or theft transactions, enter any insurance
                  payments you have received or anticipate receiving, regardless
                  of whether a claim has been filed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For property held under Section 1255:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the property was{' '}
                    <span style={{ fontWeight: 600 }}> sold or exchanged,</span>{' '}
                    enter the{' '}
                    <span style={{ fontWeight: 600 }}> amount realized</span>.
                  </li>
                  <li>
                    If the property was disposed of in{' '}
                    <span style={{ fontWeight: 600 }}> any other manner</span>,
                    enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      FMV of the property
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the adjusted cost or basis of the property after
                  reducing it for certain credits, such as the enhanced oil
                  recovery credit or disabled access credit. Do not reduce the
                  basis for items already included in line 22 calculations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    depreciation, amortization, and other deductions
                  </span>{' '}
                  previously claimed for the property on this line.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Add all applicable deductions and basis reductions</li>
                  <li>
                    From the above total, subtract any applicable recapture
                    amounts or basis increases
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Additional depreciation adjustments may also be required if
                  another asset's adjusted basis was used to determine the basis
                  of the property reported on line 19.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    adjusted basis of the property
                  </span>{' '}
                  by subtracting line 22 from line 21. For Section 1255
                  property, enter the adjusted basis of the Section 126 property
                  disposed of.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 24
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 23 from line 20 to calculate the total gain from
                  the property disposition.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 25a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation or amortization allowed or
                  allowable for the Section 1245 property reported on line 19.
                  This may include depreciation deductions, Section 179 expense
                  deductions, amortization, and certain other deductions
                  previously claimed for the property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Section 1245 property generally includes depreciable personal
                  property, certain business equipment, machinery, storage
                  facilities, and specific real property used in business
                  activities.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 25b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller of line 24 or line 25a. This amount is
                  generally treated as ordinary income under the Section 1245
                  depreciation recapture rules.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part3-line26-img.webp" />
                  <source srcSet="/assets/images/f4797-part3-line26-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part3-line26-img.png"
                    loading="lazy"
                    alt="Form 4797 Part III Gain from Disposition of Property from Line 26 to Line 29b"
                    width={751}
                    height={417}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line pertains to Section 1250 property that normally
                  covers real property like buildings. If straight-line
                  depreciation was used, line 26g is generally entered as zero
                  unless special corporate recapture rules apply.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    additional depreciation
                  </span>{' '}
                  claimed <span style={{ fontWeight: 600 }}> after 1975</span>.
                  Additional depreciation is the amount by which actual
                  depreciation, including any special depreciation allowance or
                  commercial revitalization deduction, exceeds the depreciation
                  calculated using the straight-line method.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In most cases, use 100% as the applicable percentage for this
                  line. However, different percentages may apply for certain
                  low-income rental housing properties under{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Section 1250(a)(1)(B)
                  </span>{' '}
                  rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 26a from line 24 to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    calculate the balance amount
                  </span>
                  . If the property is residential rental property, or if line
                  24 is not greater than line 26a, skip lines 26d and 26e.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    additional depreciation
                  </span>{' '}
                  claimed <span style={{ fontWeight: 600 }}>
                    {' '}
                    after 1969
                  </span>{' '}
                  and <span style={{ fontWeight: 600 }}> before 1976.</span>
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount from line 26c or line 26d.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporations subject to Section 291 must{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    report 20% of the excess amount
                  </span>{' '}
                  that would have been treated as ordinary income if the
                  property were considered Section 1245 property instead of
                  Section 1250 property. If straight-line depreciation was used,
                  the ordinary income amount is generally 20% of the Section
                  1245 recapture amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 26g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 26b, 26e, and 26f to determine the total Section
                  1250 recapture amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter details of Section 1252 property on this line, generally
                  involving certain farmland where{' '}
                  <span style={{ fontWeight: 600 }}>
                    soil and water conservation or land clearing expenses were
                    previously deducted.
                  </span>{' '}
                  Partnerships generally do not complete this section. Instead,
                  partners must report Section 1252 amounts based on the
                  information provided by the partnership.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 27a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductible soil, water, and land clearing expenses
                  related to the property.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 27b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Multiply line 27a</span>{' '}
                  with the applicable percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 27c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount from line 24 or line 27b.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 28 applies to Section 1254 property related to oil, gas,
                  geothermal, mining, and natural resource properties.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 28a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total deductible costs previously claimed that
                  reduced the adjusted basis of Section 1254 property, including
                  oil, gas, geothermal, mining, and other natural resource
                  properties, on this line.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    For property placed in service{' '}
                    <span style={{ fontWeight: 600 }}>before 1987,</span>{' '}
                    include intangible drilling and development costs deducted
                    after 1975 that would otherwise have been added to the
                    property’s basis.
                  </li>
                  <li>
                    For property placed in service{' '}
                    <span style={{ fontWeight: 600 }}>after 1986,</span> include
                    deductions claimed under Sections 263, 616, or 617, along
                    with depletion deductions under Section 611 that reduced the
                    property’s adjusted basis.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If only part of the Section 1254 property or a partial
                  ownership interest was disposed of, special Section 1254 rules
                  may apply.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 28b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller amount from line 24 or line 28a.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 29
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter information on Line 29 if the property is Section 1255
                  property involving payments previously excluded from income
                  under Section 126.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 29a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use 100% if the property is disposed of less than 10 years
                  after receiving payments excluded from income. After the
                  10-year period, reduce the percentage by 10% for each
                  additional year or partial year the property was held. If the
                  property was held for 20 years or more, use zero.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 29b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If part of the gain on line 24 is already treated as ordinary
                  income under Sections 1231 through 1254, enter the smaller of:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 24 reduced by the gain already treated as ordinary
                    income under another section, or{' '}
                  </li>
                  <li>The amount from line 29a. </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f4797-summary-img.webp" />
                  <source srcSet="/assets/images/f4797-summary-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-summary-img.png"
                    loading="lazy"
                    alt="Form 4797 Part III Summary of Gains from Line 30 to Line 32"
                    width={751}
                    height={96}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 30
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gains for all properties reported in Part III
                  after completing the calculations for each property column.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 31
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from columns A through D for lines 25b, 26g,
                  27c, 28b, and 29b. Enter the total on line 31 and report it on
                  line 13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 32
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 31 from line 30. Enter the portion related to
                  casualty or theft from Form 4684 on line 33. Enter the
                  remaining amount from other transactions on Form 4797, line 6.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part IV - Recapture Amounts Under Sections 179 and 280F(b)(2)
                  When Business Use Drops to 50% or Less
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4797-part4-img.webp" />
                  <source srcSet="/assets/images/f4797-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4797-part4-img.png"
                    loading="lazy"
                    alt="Form 4797 Part IV showing recapture amounts when business use of Section 179 or listed property falls below 50%."
                    width={751}
                    height={116}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete column (a) if you claimed a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Section 179 deduction
                  </span>{' '}
                  for property placed in service after 1986 and the business use
                  dropped to 50% or less during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete column (b) for listed property, such as certain
                  vehicles or business-use assets, if the business use decreased
                  to 50% or less during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If multiple properties are subject to recapture, calculate
                  each one separately and attach the details to the tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 33
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (a) –</span> Enter
                    the Section 179 deduction originally claimed for the
                    property.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (b) –</span> Enter
                    the depreciation allowed in prior years, including any
                    Section 179 deduction claimed.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 34
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Column (a) –</span> Enter
                    the depreciation that would have been allowed from the year
                    the property was placed in service through the current year.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Column (b) –</span> Enter
                    the depreciation that would have been allowed if the
                    property had not been used more than 50% for business
                    purposes.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 35
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Subtract line 34 </span>{' '}
                  from
                  <span style={{ fontWeight: 600 }}> line 33</span> to determine
                  the recapture amount. Report this amount as other income on
                  the same form or schedule where the original deduction was
                  claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the property was used for both business and
                  income-producing purposes, part of the recapture amount may
                  also be subject to self-employment tax. Increase the
                  property’s basis by the recapture amount reported.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        For additional filing guidance and reporting details,
                        refer to the official IRS Form 4797{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i4797.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        and related IRS forms and publications.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the difference between a short-term gain and a
                  long-term gain on the sale of business property?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    A short-term gain is realized when you sell a business asset
                    that you held for one year or less. A long-term gain is
                    realized when you sell a business asset that you held for
                    more than one year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What types of property are reported on Form 4797?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      Form 4797 is used to report the sale or exchange of
                      business property, which includes real estate, machinery,
                      equipment, vehicles, and other assets that have been used
                      in a trade or business and have been depreciated or
                      amortized. It also covers involuntary conversions, such as
                      property destroyed by casualty or condemned.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Do I need to file Form 4797 if I sell a business asset that
                  is part of a Section 1231 transaction?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Yes, you still need to file Form 4797 to report the sale of
                    a Section 1231 asset, even if the gain or loss is treated as
                    a capital gain or loss.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What Is the Difference Between Form 8949 and Form 4797?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    <ul>
                      <li style={{ marginBottom: 8 }}>
                        <Link
                          href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 8949
                        </Link>{' '}
                        is used to report sales of investment assets such as
                        stocks, bonds, and cryptocurrency and{' '}
                      </li>
                      <li style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 600 }}> Form 4797</span> is
                        used to report the sale or disposal of business property
                        such as equipment, machinery, and rental property.
                      </li>
                    </ul>
                    In simple terms,{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 8949 applies to investment transactions,
                    </span>{' '}
                    whereas{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 4797 applies to business property transactions
                    </span>{' '}
                    .
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. Is There a Worksheet for Form 4797?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Yes. The IRS provides worksheets and calculation guidance
                    within the Form 4797 instructions for transactions such as
                    Section 179 property dispositions and depreciation
                    recapture. Depending on the transaction, additional
                    worksheets or related forms like Form 4684, Form 6252, and
                    Form 8824 may also be required.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form4797Sales;
